import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { toast } from "react-toastify";
import { useBoardData } from "./useBoardData";
import { useBoardFetch } from "./useBoardFetch";
import {
  selectSubGroupData,
  selectSubGroupingDataMemoized,
  selectGroupingDataMemoized,
  selectColumns,
  selectPagination,
  selectHasMoreMap,
  selectLoadingStatuses,
  selectApiData,
  selectGroupingConfig,
  selectDebouncedSearchQuery,
  selectFilters,
  selectBoardState,
  selectActiveSprintIds,
} from "@/features/bughurd/boardSelector";
import {
  updateTask,
  setSubGroupLoadingState,
  updateSubGroupData,
  setLoadingStatus,
  setPagination,
  updateColumn,
  setHasMore,
} from "@/features/bughurd/slice";
import { useLazyGetListOfBugsQuery } from "../../../../features/bughurd/api";

interface TaskUpdateHandler {
  (taskId: string, updatedTask: any | null, newColumnId?: string): void;
}

export const useTaskOperations = (id?: string, isMyTaskView?: boolean) => {
  const { id: paramId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { currentUser } = useAppSelector((state) => state.appGlobal);

  const {
    fetchInProgress,
    PAGE_LIMIT,
    preserveScrollPosition,
    restoreScrollPosition,
  } = useBoardData();

  const { fetchBugsForGroup } = useBoardFetch();
  const [getBugsByStatus] = useLazyGetListOfBugsQuery();

  // Selectors
  const subGroupData = useAppSelector(selectSubGroupData);
  const subGroupingData = useAppSelector(selectSubGroupingDataMemoized);
  const groupingData = useAppSelector(selectGroupingDataMemoized);
  const columns = useAppSelector(selectColumns);
  const pagination = useAppSelector(selectPagination);
  const hasMoreMap = useAppSelector(selectHasMoreMap);
  const loadingStatuses = useAppSelector(selectLoadingStatuses);
  const apiData = useAppSelector(selectApiData);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const debouncedSearchQuery = useAppSelector(selectDebouncedSearchQuery);
  const filters = useAppSelector(selectFilters);
  const boardState = useAppSelector(selectBoardState);
  const activeSprintIds = useAppSelector(selectActiveSprintIds);

  const { tagList, projects } = apiData;
  const { groupBy, subGroupBy, sortOrder } = groupingConfig;
  const {
    selectedTags,
    selectedAvatars,
    selectedPriorities,
    selectedStatuses,
    selectedProjects,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
  } = filters;
  const { projectDetails } = boardState;

  const boardType = projectDetails?.boardType;

  // Helper function to get filtered projects for MyTaskView
  const getFilteredProjects = useCallback(() => {
    if (!isMyTaskView) return undefined;

    // If there are selected projects in filters, use only those
    if (selectedProjects && selectedProjects.length > 0) {
      return selectedProjects;
    }

    // Otherwise, use all available projects
    return projects?.map((item) => item._id);
  }, [isMyTaskView, selectedProjects, projects]);

  const handleTaskUpdate = useCallback<TaskUpdateHandler>(
    (taskId, updatedTask, newColumnId = null) => {
      dispatch(
        updateTask({
          taskId,
          updatedTask,
          newColumnId,
          subGroupId:
            subGroupBy !== "None" ? Object.keys(subGroupData)[0] : undefined,
        })
      );
    },
    [subGroupBy, subGroupData]
  );

  // Enhanced handleLoadMore for subgroups
  const handleLoadMore = useCallback(
    (columnId: string, subGroupId?: string) => {
      const fetchKey = subGroupId ? `${subGroupId}-${columnId}` : columnId;

      if (fetchInProgress.current[fetchKey]) {
        return;
      }

      if (subGroupId) {
        // Handle sub-group load more
        const subGroupInfo = subGroupingData[subGroupId];
        const groupInfo = groupingData[columnId];
        const subGroup = subGroupData[subGroupId];

        if (!subGroupInfo || !groupInfo) {
          return;
        }

        if (!subGroup || !subGroup.columns[columnId]) {
          return;
        }

        // Check if we have more items to load
        const hasMore = subGroup.hasMore?.[columnId];
        const isLoadingMore =
          subGroup.loadingStatuses?.[columnId]?.isLoadingMore;

        if (hasMore === false) {
          return;
        }

        if (isLoadingMore) {
          return;
        }

        const currentPage = subGroup.pagination?.[columnId]?.page || 1;
        const limit = subGroup.pagination?.[columnId]?.limit || PAGE_LIMIT;
        const nextPage = currentPage + 1;

        const currentItemsCount =
          subGroup.columns[columnId]?.tasks?.length || 0;
        const totalItems = subGroup.pagination?.[columnId]?.total || 0;

        if (currentItemsCount >= totalItems) {
          // Update hasMore to false
          dispatch(
            updateSubGroupData({
              subGroupId,
              data: {
                hasMore: {
                  ...subGroup.hasMore,
                  [columnId]: false,
                },
              },
            })
          );

          return;
        }

        fetchBugsForGroup(
          columnId,
          groupInfo,
          nextPage,
          limit,
          true, // append = true for load more
          false,
          subGroupId,
          subGroupInfo
        );
      } else {
        // Handle regular column load more (existing logic)
        if (fetchInProgress.current[columnId] || !hasMoreMap[columnId]) {
          return;
        }

        const groupData = groupingData[columnId];

        if (!groupData) {
          return;
        }

        const currentPage = pagination[columnId]?.page || 1;
        const limit = pagination[columnId]?.limit || PAGE_LIMIT;
        const nextPage = currentPage + 1;

        const currentItemsCount = columns[columnId]?.tasks?.length || 0;
        const totalItems = pagination[columnId]?.total || 0;

        if (currentItemsCount >= totalItems) {
          dispatch(
            setHasMore({
              key: columnId,
              hasMore: false,
            })
          );

          return;
        }

        fetchBugsForGroup(columnId, groupData, nextPage, limit, true);
      }
    },
    [
      fetchBugsForGroup,
      hasMoreMap,
      pagination,
      groupingData,
      columns,
      subGroupData,
      subGroupingData,
      fetchInProgress,
      PAGE_LIMIT,
    ]
  );

  // Enhanced handlePageChange for subgroups
  const handlePageChange = useCallback(
    async (
      columnId: string | null,
      paginationModel: any,
      viewType: string,
      subGroupId?: string
    ) => {
      if (viewType !== "list" && viewType !== "table") {
        return;
      }

      // Handle non-grouped view (columnId is null)
      if (!columnId || columnId === "all-tasks") {
        // Update the global pagination state
        dispatch(
          setPagination({
            key: "global",
            pagination: {
              page: paginationModel.page,
              limit: paginationModel.pageSize || paginationModel.limit,
            },
          })
        );

        return;
      }

      if (subGroupId) {
        // Handle sub-group pagination
        const subGroupInfo = subGroupingData[subGroupId];
        const groupInfo = groupingData[columnId];

        if (!subGroupInfo || !groupInfo) {
          return;
        }

        const fetchKey = `${subGroupId}-${columnId}`;

        if (fetchInProgress.current[fetchKey]) {
          return;
        }

        try {
          fetchInProgress.current[fetchKey] = true;

          dispatch(
            updateSubGroupData({
              subGroupId,
              data: {
                loadingStatuses: {
                  ...subGroupData[subGroupId]?.loadingStatuses,
                  [columnId]: {
                    isInitialLoading: true,
                    isLoadingMore: false,
                  },
                },
              },
            })
          );

          await fetchBugsForGroup(
            columnId,
            groupInfo,
            paginationModel.page,
            paginationModel.pageSize || paginationModel.limit,
            false,
            false,
            subGroupId,
            subGroupInfo
          );
        } catch (error) {
          console.error(
            `Error fetching page ${paginationModel.page} for sub-group ${subGroupInfo.title} - ${groupInfo.title}:`,
            error
          );

          dispatch(
            updateSubGroupData({
              subGroupId,
              data: {
                loadingStatuses: {
                  ...subGroupData[subGroupId]?.loadingStatuses,
                  [columnId]: {
                    isInitialLoading: false,
                    isLoadingMore: false,
                  },
                },
              },
            })
          );

          toast.error("Failed to load page data");
        } finally {
          fetchInProgress.current[fetchKey] = false;
        }
      } else {
        // Handle grouped view (existing logic)
        const groupData = groupingData[columnId];

        if (!groupData) {
          return;
        }

        if (fetchInProgress.current[columnId]) {
          return;
        }

        try {
          fetchInProgress.current[columnId] = true;

          dispatch(
            setLoadingStatus({
              key: columnId,
              loading: {
                isInitialLoading: true,
                isLoadingMore: false,
              },
            })
          );

          const queryParams: any = {
            id: isMyTaskView ? "" : paramId,
            forBoard: true,
            search: debouncedSearchQuery || "",
            page: paginationModel.page,
            limit: paginationModel.pageSize || paginationModel.limit,
            timestamp: new Date().getTime(),
            sortBy: "createdAt",
            sortType: sortOrder === "Ascending" ? "ASC" : "DESC",
            ...(dueDateStart && { dueDateStart }),
            ...(dueDateEnd && { dueDateEnd }),
            ...(startDateStart && { startDateStart }),
            ...(startDateEnd && { startDateEnd }),
            ...(updatedAtStart && { updatedAtStart }),
            ...(updatedAtEnd && { updatedAtEnd }),
            isRecurring: isRecurring || false,
          };

          // Handle project filtering
          if (isMyTaskView) {
            const filteredProjects = getFilteredProjects();
            if (filteredProjects && filteredProjects.length > 0) {
              queryParams.project = filteredProjects;
            }
          } else {
            // For regular project view, if project filter is selected and we're not grouping by project
            if (
              selectedProjects &&
              selectedProjects.length > 0 &&
              groupData.type !== "project"
            ) {
              queryParams.project = selectedProjects;
            }
          }

          // For My Tasks view, always include current user in assignTo
          if (isMyTaskView && currentUser?._id) {
            if (groupData.type === "assignee") {
              queryParams.assignTo = [columnId];
            } else {
              if (selectedAvatars?.length > 0) {
                queryParams.assignTo = selectedAvatars;
              } else {
                queryParams.assignTo = [currentUser._id];
              }
            }
          } else {
            if (groupData.type !== "tag" && selectedTags?.length > 0) {
              if (tagList?.length > 0) {
                const tagNames = selectedTags
                  .map((tagId) => {
                    const tag = tagList.find((t: any) => t.tagName === tagId);
                    return tag ? tag.tagName : null;
                  })
                  .filter(Boolean);
                if (tagNames.length > 0) {
                  queryParams.tags = tagNames;
                }
              }
            }

            if (groupData.type !== "assignee" && selectedAvatars?.length > 0) {
              queryParams.assignTo = selectedAvatars;
            }

            if (
              groupData.type !== "priority" &&
              selectedPriorities?.length > 0
            ) {
              queryParams.severities = selectedPriorities;
            }

            if (groupData.type !== "status" && selectedStatuses?.length > 0) {
              queryParams.statuses = selectedStatuses;
            }
          }

          fetchBugsForGroup(
            columnId,
            groupData,
            paginationModel.page,
            paginationModel.pageSize || paginationModel.limit,
            false
          );
        } catch (error) {
          console.error(
            `Error fetching page ${paginationModel.page} for group ${groupData.title}:`,
            error
          );

          dispatch(
            setLoadingStatus({
              key: columnId,
              loading: {
                isInitialLoading: false,
                isLoadingMore: false,
              },
            })
          );

          toast.error("Failed to load page data");
        } finally {
          fetchInProgress.current[columnId] = false;
        }
      }
    },
    [
      paramId,
      isMyTaskView,
      currentUser,
      debouncedSearchQuery,
      selectedAvatars,
      selectedTags,
      selectedProjects,
      getBugsByStatus,
      groupingData,
      selectedPriorities,
      sortOrder,
      dueDateStart,
      dueDateEnd,
      startDateStart,
      startDateEnd,
      isRecurring,
      updatedAtStart,
      updatedAtEnd,
      selectedStatuses,
      tagList,
      subGroupingData,
      fetchBugsForGroup,
      columns,
      subGroupData,
      projects,
      fetchInProgress,
      getFilteredProjects,
    ]
  );

  const refreshColumnData = useCallback(
    async (columnId?: string, subGroupId?: string, paginationModel?: any) => {
      // Only skip refresh for SCRUM boards with no active sprints when doing a full board refresh
      // Allow specific column refreshes (e.g., after creating a task) to proceed
      if (boardType === "SCRUM" && activeSprintIds.length === 0 && !columnId) {
        return;
      }

      if (columnId) {
        if (subGroupId) {
          // Refresh sub-group column data
          const subGroupInfo = subGroupingData[subGroupId];
          const groupInfo = groupingData[columnId];

          if (subGroupInfo && groupInfo) {
            preserveScrollPosition(columnId);

            dispatch(
              updateSubGroupData({
                subGroupId,
                data: {
                  pagination: {
                    ...subGroupData[subGroupId]?.pagination,
                    [columnId]: {
                      page: 1,
                      limit: PAGE_LIMIT,
                    },
                  },
                  hasMore: {
                    ...subGroupData[subGroupId]?.hasMore,
                    [columnId]: true,
                  },
                  loadingStatuses: {
                    ...subGroupData[subGroupId]?.loadingStatuses,
                    [columnId]: {
                      isInitialLoading: true,
                      isLoadingMore: false,
                    },
                  },
                },
              })
            );

            try {
              await fetchBugsForGroup(
                columnId,
                groupInfo,
                1,
                PAGE_LIMIT,
                false,
                true,
                subGroupId,
                subGroupInfo
              );
            } catch (error) {
              console.error(`Error refreshing sub-group column data: ${error}`);
            }
          }
        } else {
          // Refresh regular column data
          const groupData = groupingData[columnId];

          if (groupData) {
            preserveScrollPosition(columnId);

            dispatch(
              setPagination({
                key: columnId,
                pagination: {
                  page: 1,
                  limit: PAGE_LIMIT,
                },
              })
            );

            dispatch(
              setHasMore({
                key: columnId,
                hasMore: true,
              })
            );

            dispatch(
              setLoadingStatus({
                key: columnId,
                loading: {
                  isInitialLoading: true,
                  isLoadingMore: false,
                },
              })
            );

            try {
              await fetchBugsForGroup(
                columnId,
                groupData,
                paginationModel?.page || 1,
                paginationModel?.pageSize || PAGE_LIMIT,
                false,
                true
              );
            } catch (error) {
              console.error(`Error refreshing column data: ${error}`);
            }
          } else {
            // Column doesn't exist in groupingData, refresh all columns as fallback
            console.warn(
              `Column ${columnId} not found in groupingData, refreshing all columns`
            );
            if (Object.keys(groupingData).length > 0) {
              const refreshPromises = Object.keys(groupingData).map(
                (groupId) => {
                  const groupDataItem = groupingData[groupId];
                  dispatch(
                    setPagination({
                      key: groupId,
                      pagination: {
                        page: 1,
                        limit: PAGE_LIMIT,
                      },
                    })
                  );

                  dispatch(
                    setHasMore({
                      key: groupId,
                      hasMore: true,
                    })
                  );

                  dispatch(
                    setLoadingStatus({
                      key: groupId,
                      loading: {
                        isInitialLoading: true,
                        isLoadingMore: false,
                      },
                    })
                  );

                  return fetchBugsForGroup(
                    groupId,
                    groupDataItem,
                    1,
                    PAGE_LIMIT,
                    false,
                    true
                  );
                }
              );

              try {
                await Promise.all(refreshPromises);
              } catch (error) {
                console.error(`Error refreshing all columns: ${error}`);
              }
            }
          }
        }
      } else {
        // Refresh all data
        if (groupBy === "None") {
          dispatch(
            setPagination({
              key: "global",
              pagination: {
                page: 1,
                limit: PAGE_LIMIT,
              },
            })
          );
        } else if (Object.keys(groupingData).length > 0) {
          Object.keys(groupingData).forEach((groupId) => {
            preserveScrollPosition(groupId);
          });

          Object.keys(groupingData).forEach((groupId) => {
            dispatch(
              setPagination({
                key: groupId,
                pagination: {
                  page: 1,
                  limit: PAGE_LIMIT,
                },
              })
            );

            dispatch(
              setHasMore({
                key: groupId,
                hasMore: true,
              })
            );

            dispatch(
              setLoadingStatus({
                key: groupId,
                loading: {
                  isInitialLoading: false,
                  isLoadingMore: false,
                },
              })
            );
          });

          try {
            const refreshPromises = Object.keys(groupingData).map((groupId) => {
              const groupData = groupingData[groupId];
              return fetchBugsForGroup(
                groupId,
                groupData,
                1,
                PAGE_LIMIT,
                false
              );
            });

            await Promise.all(refreshPromises);
          } catch (error) {
            console.error(`Error refreshing all columns: ${error}`);
          }
        }
      }
    },
    [
      groupingData,
      subGroupingData,
      fetchBugsForGroup,
      preserveScrollPosition,
      groupBy,
      subGroupData,
      PAGE_LIMIT,
      activeSprintIds,
      boardType,
    ]
  );

  return {
    handleTaskUpdate,
    handleLoadMore,
    handlePageChange,
    refreshColumnData,
  };
};
